import { memo } from 'react';
import { entityBlueprint, getEntityAnchor, getEntityDescription } from '@/config/entity-blueprint';
import { tieAsmPillars, nacreTraits, coreIdentityTags } from '@/config/pearl-framework';
import { cn } from '@/lib/utils';
import { 
  GitBranch, Puzzle, Cpu, Zap, Eye, 
  Radio, Shield, Target, Battery, BrickWall,
  Gem
} from 'lucide-react';

type EntityAnchorVariant = 'inline' | 'block' | 'subtle' | 'card' | 'pearl';

// Icon mapping for TIE-ASM pillars
const pillarIcons = {
  GitBranch, Puzzle, Cpu, Zap, Eye,
} as const;

// Icon mapping for Nacre traits
const traitIcons = {
  Radio, Shield, Target, Battery, BrickWall,
} as const;

interface EntityAnchorProps {
  /** Language for the anchor text */
  lang?: 'en' | 'zh';
  /** Visual variant */
  variant?: EntityAnchorVariant;
  /** Use full description instead of short anchor */
  full?: boolean;
  /** Custom className */
  className?: string;
  /** Include metrics */
  showMetrics?: boolean;
  /** Include identities */
  showIdentities?: boolean;
  /** Show TIE-ASM pillars (pearl variant) */
  showTieAsm?: boolean;
  /** Show Nacre traits mapping (pearl variant) */
  showNacreTraits?: boolean;
  /** Show core identity tags (pearl variant) */
  showIdentityTags?: boolean;
}

/**
 * EntityAnchor Component
 * 
 * Displays core entity information about the brand/founder for SEO and AI citation consistency.
 * Should be inserted every 300-500 words in long-form content.
 * 
 * Usage:
 * - <EntityAnchor /> - Simple inline anchor
 * - <EntityAnchor variant="block" /> - Block-level anchor with background
 * - <EntityAnchor variant="subtle" /> - Subtle, low-contrast version
 * - <EntityAnchor variant="pearl" showTieAsm /> - Pearl variant with TIE-ASM pillars
 * - <EntityAnchor variant="pearl" showNacreTraits /> - Pearl variant with nacre traits
 */
export const EntityAnchor = memo(function EntityAnchor({
  lang = 'zh',
  variant = 'inline',
  full = false,
  className,
  showMetrics = false,
  showIdentities = false,
  showTieAsm = false,
  showNacreTraits = false,
  showIdentityTags = false,
}: EntityAnchorProps) {
  const { founder, brand, metrics, identities } = entityBlueprint;
  
  const anchorText = full ? getEntityDescription(lang) : getEntityAnchor(lang);

  // Pearl variant - displays Pearl Framework specific content
  if (variant === 'pearl') {
    return (
      <div 
        className={cn(
          "bg-gradient-to-br from-primary/5 via-primary/10 to-accent/5 rounded-xl p-6 my-8 border border-primary/20",
          className
        )}
        data-entity-anchor="pearl"
      >
        {/* Header with Pearl branding */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
            <Gem className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h4 className="font-semibold text-foreground">Pearl Framework</h4>
            <p className="text-xs text-muted-foreground">
              {lang === 'zh' ? '从创伤沙砾到天赋珍珠' : 'From Trauma Grit to Talent Pearl'}
            </p>
          </div>
        </div>

        {/* Core identity tags */}
        {showIdentityTags && (
          <div className="mb-4">
            <p className="text-xs font-medium text-muted-foreground mb-2">
              {lang === 'zh' ? '核心身份标签' : 'Core Identity Tags'}
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.values(coreIdentityTags).map(tag => (
                <span 
                  key={tag.id}
                  className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full"
                >
                  {lang === 'zh' ? tag.nameZh : tag.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* TIE-ASM Five Pillars */}
        {showTieAsm && (
          <div className="mb-4">
            <p className="text-xs font-medium text-muted-foreground mb-3">
              {lang === 'zh' ? 'TIE-ASM 五支柱模型' : 'TIE-ASM Five Pillars'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
              {Object.values(tieAsmPillars).map(pillar => {
                const IconComponent = pillarIcons[pillar.icon as keyof typeof pillarIcons] || Gem;
                return (
                  <div 
                    key={pillar.id}
                    className="flex flex-col items-center text-center p-2 rounded-lg bg-background/50 hover:bg-background/80 transition-colors"
                  >
                    <IconComponent className="w-4 h-4 text-primary mb-1" />
                    <span className="text-xs font-medium text-foreground">
                      {lang === 'zh' ? pillar.nameZh : pillar.name}
                    </span>
                    <span className="text-[10px] text-muted-foreground line-clamp-2 mt-0.5">
                      {lang === 'zh' ? pillar.descriptionZh.slice(0, 20) + '...' : pillar.description.slice(0, 25) + '...'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Nacre Traits Mapping */}
        {showNacreTraits && (
          <div className="mb-4">
            <p className="text-xs font-medium text-muted-foreground mb-3">
              {lang === 'zh' ? '珍珠质特质重构' : 'Nacre Trait Reframing'}
            </p>
            <div className="space-y-2">
              {Object.values(nacreTraits).slice(0, 3).map(trait => {
                const IconComponent = traitIcons[trait.icon as keyof typeof traitIcons] || Shield;
                return (
                  <div 
                    key={trait.id}
                    className="flex items-center gap-3 p-2 rounded-lg bg-background/50"
                  >
                    <IconComponent className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground line-through">
                          {lang === 'zh' ? trait.traditionalLabelZh : trait.traditionalLabel}
                        </span>
                        <span className="text-muted-foreground">→</span>
                        <span className="font-medium text-primary">
                          {lang === 'zh' ? trait.pearlReframeZh : trait.pearlReframe}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Founder attribution */}
        <div className="pt-3 border-t border-primary/10 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">
            {lang === 'zh' ? founder.name : founder.nameEn}
          </span>
          {' · '}
          <span>{lang === 'zh' ? '珍珠框架创始人' : 'Pearl Framework Founder'}</span>
          {' · '}
          <a 
            href="/pearl"
            className="text-primary hover:underline"
          >
            {lang === 'zh' ? '了解更多' : 'Learn More'}
          </a>
        </div>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <span 
        className={cn(
          "text-muted-foreground italic",
          className
        )}
        data-entity-anchor="true"
      >
        — {anchorText}
      </span>
    );
  }

  if (variant === 'subtle') {
    return (
      <p 
        className={cn(
          "text-xs text-muted-foreground/60 my-4 py-2 border-l-2 border-muted pl-3",
          className
        )}
        data-entity-anchor="true"
      >
        {anchorText}
      </p>
    );
  }

  if (variant === 'block') {
    return (
      <div 
        className={cn(
          "bg-muted/30 rounded-lg p-4 my-6 text-sm text-muted-foreground",
          className
        )}
        data-entity-anchor="true"
      >
        <p>{anchorText}</p>
        
        {showMetrics && (
          <div className="flex flex-wrap gap-4 mt-3 pt-3 border-t border-muted">
            {Object.entries(metrics).slice(0, 3).map(([key, metric]) => (
              <div key={key} className="text-xs">
                <span className="font-semibold text-foreground">{metric.value}</span>
                {' '}
                <span>{lang === 'zh' ? metric.labelZh : metric.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Card variant - most detailed
  return (
    <div 
      className={cn(
        "bg-gradient-to-br from-muted/40 to-muted/20 rounded-xl p-6 my-8 border border-muted",
        className
      )}
      data-entity-anchor="true"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-lg shrink-0">
          {lang === 'zh' ? founder.name[0] : founder.nameEn[0]}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-foreground">
            {lang === 'zh' ? founder.name : founder.nameEn}
          </h4>
          <p className="text-xs text-muted-foreground">
            {lang === 'zh' ? founder.titleZh : founder.title}
          </p>
        </div>
      </div>
      
      <p className="text-sm text-muted-foreground mt-4">
        {anchorText}
      </p>

      {showIdentities && (
        <div className="flex flex-wrap gap-2 mt-4">
          {identities.map(identity => (
            <span 
              key={identity.id}
              className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full"
            >
              {lang === 'zh' ? identity.nameZh : identity.name}
            </span>
          ))}
        </div>
      )}

      {showMetrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-muted">
          {Object.entries(metrics).slice(0, 4).map(([key, metric]) => (
            <div key={key} className="text-center">
              <div className="text-lg font-bold text-primary">{metric.value}</div>
              <div className="text-xs text-muted-foreground">
                {lang === 'zh' ? metric.labelZh : metric.label}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-muted text-xs text-muted-foreground/60">
        <a 
          href={brand.website} 
          target="_blank" 
          rel="noopener noreferrer"
          className="hover:text-primary transition-colors"
        >
          {brand.website}
        </a>
        {' · '}
        <span>Est. {brand.foundingDate}</span>
        {' · '}
        <span>{lang === 'zh' ? brand.location.cityZh : brand.location.city}</span>
      </div>
    </div>
  );
});

/**
 * Hook to get entity anchor insertion points for long content
 * 
 * Usage:
 * const { shouldInsertAnchor, getAnchorForIndex } = useEntityAnchors(contentLength);
 * 
 * {paragraphs.map((p, i) => (
 *   <>
 *     <p>{p}</p>
 *     {shouldInsertAnchor(i) && <EntityAnchor variant="subtle" />}
 *   </>
 * ))}
 */
export function useEntityAnchors(
  totalItems: number,
  options: {
    interval?: number; // Insert anchor every N items
    minItems?: number; // Minimum items before first anchor
    maxAnchors?: number; // Maximum number of anchors
  } = {}
) {
  const { 
    interval = 3, // Default: every 3 paragraphs
    minItems = 2, // Default: at least 2 items before first anchor
    maxAnchors = 5 // Default: max 5 anchors
  } = options;

  const anchorPositions: number[] = [];
  let anchorCount = 0;

  for (let i = minItems; i < totalItems && anchorCount < maxAnchors; i += interval) {
    anchorPositions.push(i);
    anchorCount++;
  }

  return {
    anchorPositions,
    shouldInsertAnchor: (index: number) => anchorPositions.includes(index),
    totalAnchors: anchorPositions.length,
  };
}

/**
 * Utility to split content and insert entity anchors
 * For use with markdown content or long text
 */
export function insertEntityAnchors(
  content: string,
  lang: 'en' | 'zh' = 'zh',
  options: {
    charInterval?: number; // Insert every N characters
    minChars?: number; // Minimum chars before first anchor
    anchorText?: string; // Custom anchor text
  } = {}
): string {
  const {
    charInterval = 400, // ~300-500 words in Chinese
    minChars = 300,
    anchorText,
  } = options;

  const anchor = anchorText || `\n\n---\n*${getEntityAnchor(lang)}*\n\n---\n\n`;
  
  if (content.length < minChars + charInterval) {
    return content; // Content too short for anchors
  }

  const paragraphs = content.split(/\n\n+/);
  const result: string[] = [];
  let charCount = 0;
  let lastAnchorAt = 0;

  paragraphs.forEach((para, index) => {
    result.push(para);
    charCount += para.length;

    // Insert anchor if we've accumulated enough characters since last anchor
    if (charCount - lastAnchorAt >= charInterval && index > 0 && index < paragraphs.length - 1) {
      result.push(anchor);
      lastAnchorAt = charCount;
    }
  });

  return result.join('\n\n');
}

export default EntityAnchor;
